"use client"

import type { FinanceHomeOverview } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"

/**
 * Where the money goes (D4): this period's expenses by account, largest first. Transfers are not spending and
 * are not here. Bar lengths are relative to the largest line; the shares themselves were allocated server-side.
 */
export function SpendingTile({ overview, detailLink }: { overview: FinanceHomeOverview; detailLink: HomeTileLink }) {
  const t = useTranslations("dashboard.home.spending")
  const figures = overview.figures
  const rows = figures?.spendingByCategory ?? []
  const largest = Math.max(1, ...rows.map((row) => row.shareBp))

  return (
    <HomeTile title={t("title")} subtitle={t("subtitle")} link={detailLink} testId='home-tile-spending'>
      {rows.length === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <ul className='flex flex-col gap-2.5'>
          {rows.map((row) => (
            <li key={row.accountingAccountId} data-testid='spending-row' className='flex flex-col gap-1'>
              <span className='flex gap-2 text-[12.5px]'>
                <span className='truncate text-muted-foreground'>{row.name}</span>
                <span className='ml-auto font-bold tabular-nums'>{formatCurrency(row.amount, overview.currency)}</span>
              </span>
              <span className='h-2 overflow-hidden rounded-[4px] bg-muted'>
                <span
                  className='block h-full rounded-[4px] bg-rose-600 dark:bg-rose-400'
                  style={{ width: `${Math.max(2, (row.shareBp / largest) * 100)}%` }}
                />
              </span>
            </li>
          ))}
          {figures && Number(figures.otherSpending) > 0 ? (
            <li className='flex text-[12px] text-muted-foreground'>
              <span>{t("other")}</span>
              <span className='ml-auto tabular-nums'>{formatCurrency(figures.otherSpending, overview.currency)}</span>
            </li>
          ) : null}
        </ul>
      )}
    </HomeTile>
  )
}
